import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Preloader from '@/common/components/Preloader';
import {
  useCurrentDecedentId,
  useCurrentDecedentSections,
} from '@/app/providers/DecedentProvider';
import { routes } from '@/app/routing';
import {
  useFinancialInstitutionsQuery,
  useOtherCreditCardsQuery,
  useOtherFinancialLiabilitiesQuery,
} from '@/entities/question';
import {
  FinancialSection,
  FinancialSectionAddButton,
  FinancialSectionDataEntryButton,
  FinancialSectionEditButton,
} from '@/modules/financials/components';
import {
  getFinancialInstitutionColumns,
  getOtherCreditCardsColumns,
  getOtherFinancialLiabilitiesColumns,
} from '@/modules/financials/config';
import styles from '@/modules/financials/financials.styles';
import { Alert } from '@mui/material';
import { useLocalStorageState } from '@/common/hooks';

const getAlertStorageKey = (decedentId) =>
  `financialInstitutionsEmptyAlert.${decedentId}`;

const FinancialsModule = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const decedentId = useCurrentDecedentId();
  const [isAlertClosed, setIsAlertClosed] = useLocalStorageState(
    getAlertStorageKey(decedentId),
    false
  );

  const sections = useCurrentDecedentSections();
  const isMainQuestionnaireCompleted = !!sections?.main?.question?.passed;

  const {
    data: institutions = [],
    isLoading: institutionsLoading,
    refetch: refetchInstitutions,
  } = useFinancialInstitutionsQuery(decedentId, {
    enabled: !!decedentId && isMainQuestionnaireCompleted,
  });
  const {
    data: otherFinancialLiabilities,
    isLoading: liabilitiesLoading,
    refetch: refetchLiabilities,
  } = useOtherFinancialLiabilitiesQuery(decedentId, {
    enabled: !!decedentId && isMainQuestionnaireCompleted,
  });
  const {
    data: otherCreditCards,
    isLoading: creditCardsLoading,
    refetch: refetchCreditCards,
  } = useOtherCreditCardsQuery(decedentId, {
    enabled: !!decedentId && isMainQuestionnaireCompleted,
  });

  const openFinancialInstitutionDetails = useCallback(
    (institution) => {
      if (!institution?.id) {
        return;
      }

      history.push(
        routes.dashboard.finInstitutionDetails.replace(
          ':institutionId',
          String(institution.id)
        )
      );
    },
    [history]
  );

  const institutionColumns = getFinancialInstitutionColumns(t);
  const liabilitiesColumns = getOtherFinancialLiabilitiesColumns(t);
  const creditCardsColumns = getOtherCreditCardsColumns(t);
  const showEmptyInstitutionsAlert = !institutions.length && !isAlertClosed;

  const closeEmptyInstitutionsAlert = useCallback(() => {
    setIsAlertClosed(true);
  }, [setIsAlertClosed]);

  if (institutionsLoading || liabilitiesLoading || creditCardsLoading) {
    return <Preloader />;
  }

  if (!isMainQuestionnaireCompleted) {
    return (
      <>
        <Box sx={styles.pageHeader}>
          <Typography variant="h4">
            {t('Decedent’s Financial Information')}
          </Typography>
        </Box>
        <Box sx={styles.wrapper}>
          <Box sx={[styles.body, styles.incompleteQuestionnaireBody]}>
            <Typography sx={styles.incompleteQuestionnaireText}>
              {t(
                'Financial information will appear here after it has been entered during the questionnaires.'
              )}
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box sx={styles.pageHeader}>
        <Typography variant="h4">{t('Financial Institutions')}</Typography>
        {!!institutions.length && (
          <FinancialSectionEditButton
            institutions={institutions}
            refreshTask={refetchInstitutions}
          />
        )}
      </Box>
      <Box sx={styles.wrapper}>
        <Box sx={styles.body}>
          {showEmptyInstitutionsAlert && (
            <Alert severity="info" onClose={closeEmptyInstitutionsAlert}>
              {t(
                'You will be prompted to enter decedent financial information as you proceed through the questionnaires and the task modules process flow. Once entered, that information will be shown on this page.'
              )}
            </Alert>
          )}
          <FinancialSection
            type="institutions"
            title={t('Financial Institutions')}
            data={institutions}
            columns={institutionColumns}
            onEdit={openFinancialInstitutionDetails}
            addEmptyStateSlot={
              <FinancialSectionAddButton refreshTask={refetchInstitutions} />
            }
            emptyState={{
              description: t('No financial institutions yet.'),
              actionText: t('Add financial institutions'),
            }}
          />
          <FinancialSection
            type="liabilities"
            title={t('Other Financial Liabilities')}
            data={otherFinancialLiabilities}
            columns={liabilitiesColumns}
            addEmptyStateSlot={
              <FinancialSectionDataEntryButton
                data={otherFinancialLiabilities}
                refreshTask={refetchLiabilities}
                isEmpty
                addText={t('Add other financial liabilities')}
                editText={t('Edit')}
              />
            }
            headerActionSlot={
              <FinancialSectionDataEntryButton
                data={otherFinancialLiabilities}
                refreshTask={refetchLiabilities}
                addText={t('Add other financial liabilities')}
                editText={t('Edit')}
              />
            }
            emptyState={{
              description: t('No other financial liabilities yet.'),
              actionText: t('Add other financial liabilities'),
            }}
          />
          <FinancialSection
            type="creditCards"
            title={t('Other Credit Cards')}
            data={otherCreditCards}
            columns={creditCardsColumns}
            addEmptyStateSlot={
              <FinancialSectionDataEntryButton
                data={otherCreditCards}
                refreshTask={refetchCreditCards}
                isEmpty
                addText={t('Add other credit cards')}
                editText={t('Edit')}
              />
            }
            headerActionSlot={
              <FinancialSectionDataEntryButton
                data={otherCreditCards}
                refreshTask={refetchCreditCards}
                addText={t('Add other credit cards')}
                editText={t('Edit')}
              />
            }
            emptyState={{
              description: t('No other credit cards yet.'),
              actionText: t('Add other credit cards'),
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default FinancialsModule;
